import React, { useEffect, useMemo, useState } from "react";
import { DatePicker, Input, Modal, Select, Spin } from "antd";
import { SaveOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";

import * as projectsApi from "../../../../core/data/redux/services/projectsApi";
import { errMsg } from "../../helpers/errMsg.helper";
import { isValidProjectKey } from "../../helpers/issueKey.helpers";
import {
  PROJECT_PRIORITY_OPTIONS,
  PROJECT_VISIBILITY_OPTIONS,
  PROJECT_STATUS_OPTIONS,
} from "../../constants/projects.constants";
import { selectActiveCompany } from "../../../../core/data/redux/projects/selectors";
import { useSelector } from "react-redux";
import FieldLabel from "../../components/FieldLabel";
import FieldError from "../../components/FieldError";
import type { Project } from "../../../../core/data/redux/projects/types";

interface ProjectFormModalProps {
  open: boolean;
  initialProject?: Project | null;
  onSubmit: (payload: Record<string, any>) => Promise<void>;
  onCancel: () => void;
  submitting?: boolean;
}

interface FormState {
  key: string;
  name: string;
  description: string;
  priority: number | null;
  visibility: number | null;
  status: number | null;
  start_date: Dayjs | null;
  end_date: Dayjs | null;
  pro_img: string;
  logo_path: string;
  project_value: number | null;
  price_type: string;
}

type FormErrors = Partial<Record<keyof FormState | "_general", string>>;

const EMPTY_FORM: FormState = {
  key: "",
  name: "",
  description: "",
  priority: 1,
  visibility: 0,
  status: 1,
  start_date: null,
  end_date: null,
  pro_img: "",
  logo_path: "",
  project_value: null,
  price_type: "",
};

const ProjectFormModal: React.FC<ProjectFormModalProps> = ({
  open,
  initialProject,
  onSubmit,
  onCancel,
  submitting = false,
}) => {
  const isEdit = Boolean(initialProject);
  const activeCompany = useSelector(selectActiveCompany);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [keyChecking, setKeyChecking] = useState(false);
  const [keyAvailable, setKeyAvailable] = useState<boolean | null>(null);

  useEffect(() => {
    if (!open) return;
    if (initialProject) {
      setForm({
        key: initialProject.key || "",
        name: initialProject.name || "",
        description: initialProject.description || "",
        priority: initialProject.priority,
        visibility: initialProject.visibility,
        status: initialProject.status,
        start_date: initialProject.start_date
          ? dayjs(initialProject.start_date)
          : null,
        end_date: initialProject.end_date ? dayjs(initialProject.end_date) : null,
        pro_img: initialProject.pro_img || "",
        logo_path: initialProject.logo_path || "",
        project_value: initialProject.project_value,
        price_type: initialProject.price_type || "",
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setErrors({});
    setKeyAvailable(null);
  }, [open, initialProject]);

  const handleField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((p) => ({ ...p, [field]: value }));
    setErrors((p) => {
      const next = { ...p };
      delete next[field];
      return next;
    });
    if (field === "key") setKeyAvailable(null);
  };

  const checkKey = async () => {
    const trimmed = form.key.trim().toUpperCase();
    if (!isValidProjectKey(trimmed)) {
      setErrors((p) => ({
        ...p,
        key: "Key must start with a letter, 2-10 chars, A-Z and 0-9 only",
      }));
      setKeyAvailable(false);
      return;
    }
    if (isEdit && initialProject?.key === trimmed) {
      setKeyAvailable(true);
      return;
    }
    try {
      setKeyChecking(true);
      const result = await projectsApi.projectKeyAvailable(
        activeCompany.company_id,
        activeCompany.company_type,
        trimmed
      );
      setKeyAvailable(Boolean(result?.available));
      if (!result?.available) {
        setErrors((p) => ({ ...p, key: `Key '${trimmed}' is already taken` }));
      }
    } catch {
      setErrors((p) => ({ ...p, key: "Could not verify key availability" }));
      setKeyAvailable(null);
    } finally {
      setKeyChecking(false);
    }
  };

  const validate = (): boolean => {
    const errs: FormErrors = {};
    const trimmedKey = form.key.trim().toUpperCase();
    if (!trimmedKey) errs.key = "Project key is required";
    else if (!isValidProjectKey(trimmedKey))
      errs.key = "Key must start with a letter, 2-10 chars, A-Z and 0-9 only";
    else if (!isEdit && keyAvailable === false)
      errs.key = `Key '${trimmedKey}' is already taken`;

    if (!form.name.trim()) errs.name = "Project name is required";
    if (
      form.start_date &&
      form.end_date &&
      form.end_date.isBefore(form.start_date, "day")
    ) {
      errs.end_date = "End date must be after start date";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const payload: Record<string, any> = {
      key: form.key.trim().toUpperCase(),
      name: form.name.trim(),
      description: form.description || null,
      priority: form.priority,
      visibility: form.visibility,
      status: form.status,
      start_date: form.start_date ? form.start_date.format("YYYY-MM-DD") : null,
      end_date: form.end_date ? form.end_date.format("YYYY-MM-DD") : null,
      pro_img: form.pro_img || null,
      logo_path: form.logo_path || null,
      project_value: form.project_value,
      price_type: form.price_type || null,
    };
    try {
      await onSubmit(payload);
    } catch (e: any) {
      setErrors((p) => ({ ...p, _general: errMsg(e, "Submission failed") }));
    }
  };

  const keyHelp = useMemo(() => {
    if (errors.key) return <FieldError message={errors.key} />;
    if (keyChecking)
      return (
        <span className="text-muted small">
          <Spin size="small" /> Checking…
        </span>
      );
    if (keyAvailable === true)
      return <span className="text-success small">Key available</span>;
    return (
      <span className="text-muted small">
        Used in issue keys, e.g. <code>{form.key.toUpperCase() || "PROJ"}-1</code>
      </span>
    );
  }, [errors.key, keyChecking, keyAvailable, form.key]);

  const mc = isEdit
    ? { title: "Edit Project", icon: <SaveOutlined />, color: "#f59e0b" }
    : { title: "New Project", icon: <PlusOutlined />, color: "#3b82f6" };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      width="640px"
      zIndex={1001}
      destroyOnClose
      maskClosable={false}
      keyboard={false}
      centered
      styles={{
        content: {
          padding: 0,
          borderRadius: 12,
          overflow: "hidden",
        },
        header: {
          padding: "14px 20px 12px",
          borderBottom: "1px solid #f0f0f0",
          marginBottom: 0,
        },
        body: {
          padding: "16px 24px 20px",
        },
        footer: {
          padding: "10px 20px 14px",
          borderTop: "1px solid #f0f0f0",
          marginTop: 0,
        },
        mask: {
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100vh",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          background: "rgba(0,0,0,0.45)",
        },
      }}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              background: mc.color + "18",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: mc.color,
              fontSize: 15,
            }}
          >
            {mc.icon}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }}>{mc.title}</div>
            <div style={{ fontSize: 11.5, color: "#94a3b8", fontWeight: 400 }}>
              {isEdit ? `Editing: ${initialProject?.name || ""}` : "Fill in the details below"}
            </div>
          </div>
        </div>
      }
      footer={
        <div
          style={{
            padding: "14px 28px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <button
            className="btn btn-outline-danger px-4"
            style={{ borderRadius: 8 }}
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className="btn px-4"
            style={{
              borderRadius: 8,
              background: isEdit ? "#f59e0b" : "#10b981",
              color: "#fff",
              fontWeight: 600,
            }}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Saving...
              </>
            ) : isEdit ? (
              <>
                <SaveOutlined className="me-2" />
                Save Changes
              </>
            ) : (
              <>
                <PlusOutlined className="me-2" />
                Create Project
              </>
            )}
          </button>
        </div>
      }
    >
      <div className="d-flex flex-column gap-3">
        {errors._general && (
          <div
            style={{
              padding: "10px 14px",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 8,
              color: "#dc2626",
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <i className="ti ti-alert-triangle" style={{ fontSize: 16 }} />
            {errors._general}
          </div>
        )}

        <div>
          <FieldLabel required>Project Name</FieldLabel>
          <Input
            size="large"
            value={form.name}
            onChange={(e) => handleField("name", e.target.value)}
            placeholder="e.g. Customer Portal"
            status={errors.name ? "error" : ""}
            style={{ borderRadius: 8, background: "#f8fafc" }}
          />
          <FieldError message={errors.name} />
        </div>

        <div>
          <FieldLabel required>Project Key</FieldLabel>
          <div className="d-flex gap-2">
            <Input
              size="large"
              value={form.key}
              onChange={(e) =>
                handleField("key", e.target.value.toUpperCase())
              }
              placeholder="ACME"
              maxLength={10}
              status={errors.key ? "error" : ""}
              disabled={isEdit}
              style={{ borderRadius: 8, background: "#f8fafc" }}
            />
            {!isEdit && (
              <button
                type="button"
                className="btn btn-info border"
                style={{ borderRadius: 8, minHeight: 40 }}
                onClick={checkKey}
                disabled={keyChecking || !form.key.trim()}
              >
                Check
              </button>
            )}
          </div>
          <div className="mt-1">{keyHelp}</div>
        </div>

        <div>
          <FieldLabel>Description</FieldLabel>
          <Input.TextArea
            value={form.description}
            onChange={(e) => handleField("description", e.target.value)}
            placeholder="What is this project about?"
            rows={3}
            style={{ borderRadius: 8, background: "#f8fafc" }}
          />
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FieldLabel>Priority</FieldLabel>
            <Select
              style={{ width: "100%" }}
              size="large"
              value={form.priority ?? undefined}
              onChange={(v) => handleField("priority", v ?? null)}
              options={PROJECT_PRIORITY_OPTIONS}
              allowClear
            />
          </div>
          <div className="col-md-4">
            <FieldLabel>Visibility</FieldLabel>
            <Select
              style={{ width: "100%" }}
              size="large"
              value={form.visibility ?? undefined}
              onChange={(v) => handleField("visibility", v ?? null)}
              options={PROJECT_VISIBILITY_OPTIONS}
              allowClear
            />
          </div>
          <div className="col-md-4">
            <FieldLabel>Status</FieldLabel>
            <Select
              style={{ width: "100%" }}
              size="large"
              value={form.status ?? undefined}
              onChange={(v) => handleField("status", v ?? null)}
              options={PROJECT_STATUS_OPTIONS}
              allowClear
            />
          </div>
          <div className="col-md-6">
            <FieldLabel>Start Date</FieldLabel>
            <DatePicker
              size="large"
              style={{ width: "100%", borderRadius: 8, background: "#f8fafc" }}
              value={form.start_date}
              onChange={(d) => handleField("start_date", d)}
            />
          </div>
          <div className="col-md-6">
            <FieldLabel>End Date</FieldLabel>
            <DatePicker
              size="large"
              style={{ width: "100%", borderRadius: 8, background: "#f8fafc" }}
              value={form.end_date}
              onChange={(d) => handleField("end_date", d)}
              status={errors.end_date ? "error" : ""}
            />
            <FieldError message={errors.end_date} />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ProjectFormModal;
